import { useCallback, useEffect, useRef, useState } from 'react';
import { api, uploadNearImage } from '../../api.js';
import { useNearTaxonomy } from './taxonomy.js';
import { SHIP_REGIONS } from '../../../../shared/online-stores.js';
import {
  DAY_LABELS,
  EVENT_KINDS,
  nextWeekendDefault,
  plusHours,
  moveEventStart,
  typicalShopHours,
  readSavedContact,
  writeSavedContact,
  viewerZone,
  eventDate,
  eventTime,
  zoneName,
} from './helpers.js';
import { bannerBox } from './styles.js';
import {
  fieldInput,
  timeInput,
  linkBtn,
  btnPrimary,
  btnGhost,
  sectionLabel,
  chip,
  acDropdown,
  acItem,
  acAttrib,
} from './styles.js';

export function HoursEditor({ value, onChange }) {
  const days = value?.days || {};
  const setDay = (d, patch) => {
    const next = { ...days };
    if (patch.closed) delete next[d];
    else next[d] = { ...(next[d] || { open: '10:00', close: '19:00' }), ...patch };
    onChange({ days: next });
  };
  return (
    <div style={{ marginTop: 4 }}>
      {DAY_LABELS.map(([d, label]) => {
        const v = days[d];
        return (
          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
            <span style={{ width: 34, fontSize: 14, color: 'var(--text-muted)' }}>{label}</span>
            {v ? (
              <>
                <input
                  type="time"
                  value={v.open || ''}
                  onChange={(e) => setDay(d, { open: e.target.value })}
                  style={timeInput}
                />
                <span style={{ color: 'var(--text-dim)' }}>&ndash;</span>
                <input
                  type="time"
                  value={v.close || ''}
                  onChange={(e) => setDay(d, { close: e.target.value })}
                  style={timeInput}
                />
                <button type="button" onClick={() => setDay(d, { closed: true })} style={linkBtn}>
                  Closed
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setDay(d, { open: '10:00', close: '19:00' })}
                style={linkBtn}
              >
                + Set hours
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// variant 'banner' (default) shows a full 4:3 preview — right for a
// storefront/event photo. variant 'logo' shows a small square preview —
// a logo is a small badge, not a hero image, so it must not eat the
// screen while filling out the form.
// A photo still uploading when Submit was tapped was dropped: the listing
// went without it and the upload was cleared a day later. Forms hold
// Submit while any of their pickers is busy.
export function useUploading() {
  const [n, setN] = useState(0);
  const onBusy = useCallback((b) => setN((x) => Math.max(0, x + (b ? 1 : -1))), []);
  return [n > 0, onBusy];
}

export function ImagePicker({
  value,
  onChange,
  label = 'Add a photo',
  variant = 'banner',
  onBusy = null,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isLogo = variant === 'logo';
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    onBusy?.(true);
    setErr('');
    try {
      const { url } = await uploadNearImage(file);
      onChange(url);
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  };
  const previewStyle = isLogo
    ? { width: 72, height: 72, objectFit: 'cover', borderRadius: 12 }
    : { ...bannerBox, objectFit: 'cover', borderRadius: 12 };
  return (
    <div style={{ marginTop: 12 }}>
      {value ? (
        <div style={{ position: 'relative', display: isLogo ? 'inline-block' : 'block' }}>
          <img src={value} alt="" style={previewStyle} />
          <button
            type="button"
            onClick={() => onChange('')}
            className="font-heading"
            style={{
              ...btnGhost,
              position: 'absolute',
              top: isLogo ? 2 : 8,
              right: isLogo ? 2 : 8,
              padding: isLogo ? '2px 6px' : '9px 14px',
              fontSize: isLogo ? 11 : 13,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              border: 'none',
            }}
          >
            Remove
          </button>
        </div>
      ) : (
        <label style={{ ...btnGhost, display: 'flex', textAlign: 'center', cursor: 'pointer' }}>
          {busy ? 'Uploading…' : label}
          <input
            type="file"
            accept="image/*"
            onChange={pick}
            disabled={busy}
            style={{ display: 'none' }}
          />
        </label>
      )}
      {err ? (
        <div style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 6 }}>{err}</div>
      ) : null}
    </div>
  );
}

// Typeahead over /api/near/suggest (OSM). Debounced; picking a result
// hands the whole suggestion to onPick so the form pre-fills. `value` is
// the controlled text; onChange fires on every keystroke so plain typing
// (no pick) still works for places OSM doesn't know.
export function PlaceAutocomplete({ value, onChange, onPick, placeholder, origin }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const box = useRef(null);
  // Monotonic request id — a slow older response must never overwrite a
  // newer one (out-of-order fetch race).
  const seq = useRef(0);

  useEffect(() => {
    const onDoc = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      clearTimeout(timer.current);
      seq.current++; // invalidate any in-flight response after unmount
    };
  }, []);

  const search = (q) => {
    clearTimeout(timer.current);
    if (q.trim().length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      const my = ++seq.current;
      setLoading(true);
      try {
        // Pass the keeper's coarse origin so the server scopes results
        // to their country (no cross-continent matches).
        const loc =
          origin && Number.isFinite(origin.lat) ? `&lat=${origin.lat}&lng=${origin.lng}` : '';
        const r = await api(`/api/near/suggest?q=${encodeURIComponent(q)}${loc}`);
        if (my !== seq.current) return;
        setItems(r.suggestions || []);
        setOpen(true);
      } catch {
        if (my === seq.current) setItems([]);
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, 400);
  };

  return (
    <div style={{ position: 'relative' }} ref={box}>
      <input
        style={fieldInput}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => items.length && setOpen(true)}
        autoComplete="off"
      />
      {open && (items.length || loading) ? (
        <div style={acDropdown}>
          {loading && !items.length ? (
            <div style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 14 }}>
              Searching…
            </div>
          ) : null}
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              style={acItem}
              onClick={() => {
                onPick(it);
                setOpen(false);
                setItems([]);
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 15 }}>{it.name || it.address}</div>
              {it.name && it.address ? (
                <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 1 }}>
                  {it.address}
                </div>
              ) : null}
            </button>
          ))}
          {items.length ? <div style={acAttrib}>Results from OpenStreetMap</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export function CapabilityPicker({ selected, onToggle }) {
  const { labels, groups } = useNearTaxonomy();
  return (
    <>
      {groups.map((g) => (
        <div key={g.label} style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 4 }}>{g.label}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {g.keys
              .filter((k) => labels[k])
              .map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => onToggle(k)}
                  className="font-heading"
                  style={chip(!!selected[k])}
                >
                  {labels[k]}
                </button>
              ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function AddStoreForm({ coords, onDone, onCancel, kind = 'store' }) {
  const isClub = kind === 'club';
  const noun = isClub ? 'club' : 'store';
  const [f, setF] = useState({
    name: '',
    address: '',
    website: '',
    phone: '',
    contact_email: '',
    city: '',
    lat: '',
    lng: '',
    image_url: '',
    logo_url: '',
    capabilities: {},
  });
  const [hours, setHours] = useState({ days: {} });
  const [busy, setBusy] = useState(false);
  const [uploading, onUploadBusy] = useUploading();
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCap = (k) =>
    setF((s) => ({ ...s, capabilities: { ...s.capabilities, [k]: !s.capabilities[k] } }));
  // Picking a place from the name typeahead fills everything OSM knows.
  const pickPlace = (it) => {
    setF((s) => ({
      ...s,
      name: it.name || s.name,
      address: it.address || s.address,
      website: it.website || s.website,
      phone: it.phone || s.phone,
      city: it.city || s.city,
      lat: it.lat ?? s.lat,
      lng: it.lng ?? s.lng,
    }));
    if (it.hours) setHours(it.hours);
  };
  // Picking from the address typeahead fills just the location fields.
  const pickAddress = (it) => {
    setF((s) => ({
      ...s,
      address: it.address || s.address,
      city: it.city || s.city,
      lat: it.lat ?? s.lat,
      lng: it.lng ?? s.lng,
    }));
  };

  const submit = async () => {
    if (!f.name.trim()) {
      setErr(`A ${noun} name is required.`);
      return;
    }
    const hasLoc = !!f.address.trim() || (coords && typeof coords !== 'string');
    if (!hasLoc) {
      setErr(`Add the ${noun}’s address so we can place it on the map.`);
      return;
    }
    setBusy(true);
    setErr('');
    try {
      // A picked suggestion carries the store's own coordinates; otherwise
      // the typed address is geocoded server-side, with the device
      // location as the fallback.
      const picked = f.lat !== '' && f.lng !== '' && f.lat != null && f.lng != null;
      const loc = picked
        ? { picked: true }
        : coords && typeof coords !== 'string'
          ? { lat: coords.lat, lng: coords.lng }
          : {};
      await api('/api/near/places', { method: 'POST', body: { ...f, kind, hours, ...loc } });
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Could not submit. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ padding: '20px 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Thanks! <b style={{ color: 'var(--text)' }}>{f.name}</b> was submitted for review —
        it&rsquo;ll show up here once approved.
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={onDone} className="font-heading" style={btnPrimary}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 16 }}>
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
        {isClub ? 'Add a reef club' : 'Add a store'}
      </div>
      <div
        style={{ fontSize: 14, color: 'var(--text-dim)', margin: '4px 0 10px', lineHeight: 1.5 }}
      >
        Type the address and we&rsquo;ll place it on the map — no coordinates needed. An admin
        reviews new {noun}s before they appear.
      </div>
      <PlaceAutocomplete
        value={f.name}
        onChange={(v) => set('name', v)}
        onPick={pickPlace}
        placeholder={isClub ? 'Club name *' : 'Store name * (start typing to search)'}
        origin={coords && typeof coords !== 'string' ? coords : null}
      />
      <PlaceAutocomplete
        value={f.address}
        onChange={(v) => setF((s) => ({ ...s, address: v, lat: '', lng: '' }))}
        onPick={pickAddress}
        placeholder={isClub ? 'Where it meets (address or city) *' : 'Address (street & city) *'}
        origin={coords && typeof coords !== 'string' ? coords : null}
      />
      <input
        style={fieldInput}
        type="url"
        inputMode="url"
        autoComplete="url"
        placeholder={isClub ? 'Website / how to join' : 'Website'}
        value={f.website}
        onChange={(e) => set('website', e.target.value)}
      />
      <input
        style={fieldInput}
        type="tel"
        autoComplete="tel"
        placeholder="Phone"
        value={f.phone}
        onChange={(e) => set('phone', e.target.value)}
      />
      <input
        style={fieldInput}
        type="email"
        inputMode="email"
        autoComplete="off"
        placeholder={`The ${noun}'s email (optional, private)`}
        value={f.contact_email}
        onChange={(e) => set('contact_email', e.target.value)}
      />
      <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '-4px 0 0', lineHeight: 1.45 }}>
        Never shown on the listing. We use it once, to tell the {noun} it is listed.
      </p>
      <ImagePicker
        onBusy={onUploadBusy}
        value={f.image_url}
        onChange={(url) => set('image_url', url)}
        label={isClub ? '+ Add a club photo' : '+ Add a storefront photo'}
      />
      <ImagePicker
        onBusy={onUploadBusy}
        value={f.logo_url}
        onChange={(url) => set('logo_url', url)}
        label="+ Add a logo (optional)"
        variant="logo"
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginTop: 14,
        }}
      >
        <span className="font-heading" style={sectionLabel}>
          {isClub ? 'When it meets' : 'Opening hours'}
        </span>
        {!isClub ? (
          <button type="button" onClick={() => setHours(typicalShopHours())} style={linkBtn}>
            Use typical shop hours
          </button>
        ) : null}
      </div>
      <HoursEditor value={hours} onChange={setHours} />
      {!isClub ? (
        <>
          <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
            What they carry
          </div>
          <CapabilityPicker selected={f.capabilities} onToggle={toggleCap} />
        </>
      ) : null}
      {err ? (
        <div role="alert" style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 10 }}>
          {err}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          type="button"
          onClick={submit}
          disabled={busy || uploading}
          className="font-heading"
          style={{ ...btnPrimary, flex: 1, opacity: busy || uploading ? 0.6 : 1 }}
        >
          {busy ? 'Submitting…' : isClub ? 'Submit club' : 'Submit store'}
        </button>
        <button type="button" onClick={onCancel} className="font-heading" style={btnGhost}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// Suggest-an-edit — Google-Maps-style crowd correction of an existing
// place. Pre-fills from the current record; the server keeps only the
// fields that actually changed and files them as a pending suggestion
// (the live place is untouched until an admin accepts).
export function SuggestEditForm({ place, onDone, onCancel }) {
  const isClub = place.kind === 'club';
  const [f, setF] = useState({
    name: place.name || '',
    address: place.address || '',
    website: place.website || '',
    phone: place.phone || '',
    image_url: place.image_url || '',
    logo_url: place.logo_url || '',
    capabilities: { ...(place.capabilities || {}) },
  });
  const [hours, setHours] = useState(place.hours && place.hours.days ? place.hours : { days: {} });
  const [hoursTouched, setHoursTouched] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, onUploadBusy] = useUploading();
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCap = (k) =>
    setF((s) => ({ ...s, capabilities: { ...s.capabilities, [k]: !s.capabilities[k] } }));

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const patch = { ...f, ...(hoursTouched ? { hours } : {}) };
      await api(`/api/near/places/${place.id}/suggest`, {
        method: 'POST',
        body: { patch, note },
      });
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Could not submit. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ padding: '20px 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Thanks! Your suggested edit to <b style={{ color: 'var(--text)' }}>{place.name}</b> was sent
        for review. Corrections go live once an admin approves them.
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={onDone} className="font-heading" style={btnPrimary}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 16 }}>
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
        Suggest an edit
      </div>
      <div
        style={{ fontSize: 14, color: 'var(--text-dim)', margin: '4px 0 10px', lineHeight: 1.5 }}
      >
        Fix anything that&rsquo;s wrong about <b>{place.name}</b>. An admin reviews changes before
        they go live.
      </div>
      <input
        style={fieldInput}
        placeholder="Name"
        value={f.name}
        onChange={(e) => set('name', e.target.value)}
      />
      <input
        style={fieldInput}
        placeholder="Address (street & city)"
        value={f.address}
        onChange={(e) => set('address', e.target.value)}
      />
      <input
        style={fieldInput}
        type="url"
        inputMode="url"
        placeholder="Website"
        value={f.website}
        onChange={(e) => set('website', e.target.value)}
      />
      <input
        style={fieldInput}
        type="tel"
        autoComplete="tel"
        placeholder="Phone"
        value={f.phone}
        onChange={(e) => set('phone', e.target.value)}
      />
      <ImagePicker
        onBusy={onUploadBusy}
        value={f.image_url}
        onChange={(url) => set('image_url', url)}
        label="+ Update the photo"
      />
      <ImagePicker
        onBusy={onUploadBusy}
        value={f.logo_url}
        onChange={(url) => set('logo_url', url)}
        label="+ Update the logo"
        variant="logo"
      />
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
        {isClub ? 'When it meets' : 'Opening hours'}
      </div>
      <HoursEditor
        value={hours}
        onChange={(h) => {
          setHoursTouched(true);
          setHours(h);
        }}
      />
      {!isClub ? (
        <>
          <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
            What they carry
          </div>
          <CapabilityPicker selected={f.capabilities} onToggle={toggleCap} />
        </>
      ) : null}
      <textarea
        style={{ ...fieldInput, minHeight: 56, resize: 'vertical' }}
        placeholder="Note to the reviewer (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {err ? (
        <div role="alert" style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 10 }}>
          {err}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          type="button"
          onClick={submit}
          disabled={busy || uploading}
          className="font-heading"
          style={{ ...btnPrimary, flex: 1, opacity: busy || uploading ? 0.6 : 1 }}
        >
          {busy ? 'Sending…' : 'Send suggestion'}
        </button>
        <button type="button" onClick={onCancel} className="font-heading" style={btnGhost}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function AddEventForm({ coords, area = null, onDone, onCancel }) {
  // Smart default: most reef events are weekends, so pre-fill the upcoming
  // Saturday at 10:00 with a 3h window. The keeper adjusts from there
  // instead of starting at a blank picker.
  const [f, setF] = useState(() => {
    const start = nextWeekendDefault();
    return {
      title: '',
      kind: 'meetup',
      starts_at: start,
      ends_at: plusHours(start, 3),
      address: '',
      details: '',
      image_url: '',
    };
  });
  const [busy, setBusy] = useState(false);
  const [uploading, onUploadBusy] = useUploading();
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [saved, setSaved] = useState(null);
  // Editing the start takes the end along while it is still the form's own
  // 3-hour end, or would end before the new start (moveEventStart).
  const set = (k, v) => setF((s) => (k === 'starts_at' ? moveEventStart(s, v) : { ...s, [k]: v }));

  const submit = async () => {
    if (!f.title.trim()) {
      setErr('An event title is required.');
      return;
    }
    if (!f.starts_at) {
      setErr('Pick a start date and time.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const loc = coords && typeof coords !== 'string' ? { lat: coords.lat, lng: coords.lng } : {};
      // datetime-local values carry no timezone — the browser parses them
      // in the KEEPER'S zone, so convert to ISO here. Sending them raw
      // would make the server read "6:30 PM" as 6:30 PM UTC.
      const toIso = (v) => (v ? new Date(v).toISOString() : '');
      const r = await api('/api/near/events', {
        method: 'POST',
        body: {
          ...f,
          starts_at: toIso(f.starts_at),
          ends_at: toIso(f.ends_at),
          venue_text: f.address,
          tz: viewerZone(),
          ...(area?.bbox
            ? {
                area_bbox: [
                  area.bbox.minLat,
                  area.bbox.minLng,
                  area.bbox.maxLat,
                  area.bbox.maxLng,
                ].join(','),
              }
            : {}),
          ...loc,
        },
      });
      setSaved({ ...(r || {}), unpinned: r?.pinned === false && !!f.address.trim() });
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Could not submit. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ padding: '20px 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Thanks! <b style={{ color: 'var(--text)' }}>{f.title}</b> was submitted for review —
        it&rsquo;ll show up here once approved.
        {saved?.starts_at ? (
          <div style={{ marginTop: 10 }}>
            Saved for {eventDate(saved.starts_at, saved.tz)} ·{' '}
            {eventTime(saved.starts_at, saved.tz)}
            {zoneName(saved.tz) ? `, ${zoneName(saved.tz)}` : ''}
            {saved.found_at ? `, at ${saved.found_at}` : ''}.
          </div>
        ) : null}
        {saved?.unpinned ? (
          <div style={{ marginTop: 10 }}>
            We could not find that address on the map, so we will place it when we review the event.
          </div>
        ) : null}
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={onDone} className="font-heading" style={btnPrimary}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 16 }}>
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
        Add an event
      </div>
      <div
        style={{ fontSize: 14, color: 'var(--text-dim)', margin: '4px 0 10px', lineHeight: 1.5 }}
      >
        Frag swaps, club meetups, store sales — anything reef. An admin reviews new events before
        they appear.
      </div>
      <input
        style={fieldInput}
        placeholder="Event title *"
        value={f.title}
        onChange={(e) => set('title', e.target.value)}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
        {EVENT_KINDS.map((k) => (
          <button
            key={k.k}
            type="button"
            onClick={() => set('kind', k.k)}
            className="font-heading"
            style={chip(f.kind === k.k)}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
        Starts *
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-dim)', margin: '2px 0 4px' }}>
        Local time at the venue.
      </div>
      <input
        type="datetime-local"
        style={fieldInput}
        value={f.starts_at}
        onChange={(e) => set('starts_at', e.target.value)}
      />
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 12 }}>
        Ends
      </div>
      <input
        type="datetime-local"
        style={fieldInput}
        value={f.ends_at}
        onChange={(e) => set('ends_at', e.target.value)}
      />
      <input
        style={fieldInput}
        placeholder="Venue or address"
        value={f.address}
        onChange={(e) => set('address', e.target.value)}
      />
      <textarea
        style={{ ...fieldInput, minHeight: 70, resize: 'vertical' }}
        placeholder="Details (optional)"
        value={f.details}
        onChange={(e) => set('details', e.target.value)}
      />
      <ImagePicker
        onBusy={onUploadBusy}
        value={f.image_url}
        onChange={(url) => set('image_url', url)}
        label="+ Add an event photo"
      />
      {err ? (
        <div role="alert" style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 10 }}>
          {err}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          type="button"
          onClick={submit}
          disabled={busy || uploading}
          className="font-heading"
          style={{ ...btnPrimary, flex: 1, opacity: busy || uploading ? 0.6 : 1 }}
        >
          {busy ? 'Submitting…' : 'Submit event'}
        </button>
        <button type="button" onClick={onCancel} className="font-heading" style={btnGhost}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function AddFragForm({ coords, onDone, onCancel, defaultCity = '' }) {
  // Smart defaults: pre-fill the city from the keeper's set location and
  // the contact from the last listing they made, so a repeat seller barely
  // types.
  const [f, setF] = useState(() => ({
    title: '',
    species: '',
    price: '',
    trade_ok: false,
    free: false,
    contact: readSavedContact(),
    city: defaultCity || '',
    details: '',
    image_url: '',
  }));
  const [busy, setBusy] = useState(false);
  const [uploading, onUploadBusy] = useUploading();
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.title.trim()) {
      setErr('A title is required (what are you selling?).');
      return;
    }
    if (!f.city.trim() && !(coords && typeof coords !== 'string')) {
      setErr('Add your city so buyers can see how far away it is.');
      return;
    }
    if (!f.contact.trim()) {
      setErr('Add a contact (handle, phone, or email) so buyers can reach you.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      writeSavedContact(f.contact);
      const loc = coords && typeof coords !== 'string' ? { lat: coords.lat, lng: coords.lng } : {};
      await api('/api/near/frags', { method: 'POST', body: { ...f, ...loc } });
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Could not submit. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ padding: '20px 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Thanks! <b style={{ color: 'var(--text)' }}>{f.title}</b> was submitted for review —
        it&rsquo;ll show up here once approved.
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={onDone} className="font-heading" style={btnPrimary}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 16 }}>
      <div className="font-heading" style={{ ...sectionLabel, marginTop: 14 }}>
        List a frag
      </div>
      <div
        style={{ fontSize: 14, color: 'var(--text-dim)', margin: '4px 0 10px', lineHeight: 1.5 }}
      >
        Sell or trade a coral, frag, or fish with keepers near you. An admin reviews listings before
        they appear.
      </div>
      <input
        style={fieldInput}
        placeholder="What are you selling? *"
        value={f.title}
        onChange={(e) => set('title', e.target.value)}
      />
      <input
        style={fieldInput}
        placeholder="Species / coral name"
        value={f.species}
        onChange={(e) => set('species', e.target.value)}
      />
      <input
        style={{ ...fieldInput, opacity: f.free ? 0.5 : 1 }}
        type="number"
        inputMode="decimal"
        placeholder={f.free ? 'Free — no price' : 'Price ($)'}
        value={f.free ? '' : f.price}
        disabled={f.free}
        onChange={(e) => set('price', e.target.value)}
      />
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 8 }}>
        <button
          type="button"
          onClick={() => set('free', !f.free)}
          className="font-heading"
          style={chip(f.free)}
        >
          Free for pickup
        </button>
        <button
          type="button"
          onClick={() => set('trade_ok', !f.trade_ok)}
          className="font-heading"
          style={chip(f.trade_ok)}
        >
          Open to trade
        </button>
      </div>
      <input
        style={fieldInput}
        placeholder="Your city *"
        value={f.city}
        onChange={(e) => set('city', e.target.value)}
      />
      <input
        style={fieldInput}
        placeholder="Contact — handle, phone, or email *"
        value={f.contact}
        onChange={(e) => set('contact', e.target.value)}
      />
      <textarea
        style={{ ...fieldInput, minHeight: 64, resize: 'vertical' }}
        placeholder="Details (size, condition, care)"
        value={f.details}
        onChange={(e) => set('details', e.target.value)}
      />
      <ImagePicker
        onBusy={onUploadBusy}
        value={f.image_url}
        onChange={(url) => set('image_url', url)}
        label="+ Add a frag photo"
      />
      {err ? (
        <div role="alert" style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 10 }}>
          {err}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          type="button"
          onClick={submit}
          disabled={busy || uploading}
          className="font-heading"
          style={{ ...btnPrimary, flex: 1, opacity: busy || uploading ? 0.6 : 1 }}
        >
          {busy ? 'Submitting…' : 'List it'}
        </button>
        <button type="button" onClick={onCancel} className="font-heading" style={btnGhost}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// Set where to look without sharing GPS: type a ZIP / city / state, we
// geocode it server-side to a coarse origin. Also the fallback when the
// browser won't share a location (desktop, denied permission).
export function LocationBar({ label, wholeArea = false, onSet, onGps, gpsAvailable }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    const query = q.trim();
    if (!query) return;
    setBusy(true);
    setErr('');
    try {
      // Bias the lookup toward the browser locale's country ("60440" is
      // a US ZIP, not a postcode elsewhere); the server falls back to a
      // global lookup when the biased one finds nothing.
      const region = (navigator.language || '').split('-')[1] || '';
      const cc = /^[A-Za-z]{2}$/.test(region) ? `&cc=${region.toLowerCase()}` : '';
      const r = await api(`/api/near/locate?q=${encodeURIComponent(query)}${cc}`);
      onSet({ lat: r.lat, lng: r.lng }, r.label, r.area || null);
      setQ('');
    } catch (e) {
      setErr(e.message || 'Could not find that place.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          style={{ ...fieldInput, marginTop: 0 }}
          placeholder="ZIP, city, or state"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={go}
          disabled={busy}
          className="font-heading"
          style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? '…' : 'Go'}
        </button>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginTop: 6,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          {label
            ? wholeArea
              ? `Showing all of ${label}`
              : `Showing near ${label}`
            : 'Enter a location, or use your device'}
        </span>
        {gpsAvailable ? (
          <button type="button" onClick={onGps} style={linkBtn}>
            Use my location
          </button>
        ) : null}
      </div>
      {err ? (
        <div style={{ fontSize: 14, color: 'var(--alert, #d64545)', marginTop: 4 }}>{err}</div>
      ) : null}
    </div>
  );
}

// Suggest an online store: its site, where it ships, what it sells. No
// address, no hours; reviewed like every other listing.
export function AddOnlineForm({ onDone, onCancel }) {
  const { labels } = useNearTaxonomy();
  const [f, setF] = useState({
    name: '',
    website: '',
    contact_email: '',
    ships_to: [],
    capabilities: {},
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleShip = (k) =>
    setF((s) => ({
      ...s,
      ships_to: s.ships_to.includes(k) ? s.ships_to.filter((x) => x !== k) : [...s.ships_to, k],
    }));
  const toggleCap = (k) =>
    setF((s) => ({ ...s, capabilities: { ...s.capabilities, [k]: !s.capabilities[k] } }));
  const submit = async () => {
    if (!f.name.trim() || !f.website.trim()) {
      setErr('Add the store name and its website.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api('/api/near/online', { method: 'POST', body: f });
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Could not send it.');
    }
    setBusy(false);
  };
  if (done) {
    return (
      <div style={{ padding: '16px 0', lineHeight: 1.6 }}>
        Thanks. We check each suggestion before it goes live.
        <div style={{ marginTop: 12 }}>
          <button type="button" style={btnPrimary} onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
      <input
        style={fieldInput}
        placeholder="Online store name"
        value={f.name}
        onChange={(e) => set('name', e.target.value)}
      />
      <input
        style={fieldInput}
        inputMode="url"
        placeholder="Website, like reefstore.com"
        value={f.website}
        onChange={(e) => set('website', e.target.value)}
      />
      <div style={sectionLabel}>Ships to</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {SHIP_REGIONS.map((r) => (
          <button
            key={r.k}
            type="button"
            style={chip(f.ships_to.includes(r.k))}
            onClick={() => toggleShip(r.k)}
          >
            {r.label}
          </button>
        ))}
      </div>
      <div style={sectionLabel}>What they sell</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {Object.entries(labels).map(([k, label]) => (
          <button
            key={k}
            type="button"
            style={chip(!!f.capabilities[k])}
            onClick={() => toggleCap(k)}
          >
            {label}
          </button>
        ))}
      </div>
      <input
        style={fieldInput}
        type="email"
        inputMode="email"
        autoComplete="off"
        placeholder="The store's email (optional, private)"
        value={f.contact_email}
        onChange={(e) => set('contact_email', e.target.value)}
      />
      <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '-4px 0 0', lineHeight: 1.45 }}>
        Never shown on the listing. We use it once, to tell the store it is listed.
      </p>
      {err ? (
        <div role="alert" style={{ color: 'var(--alert, #d64545)', fontSize: 15 }}>
          {err}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={{ ...btnPrimary, flex: 1 }} disabled={busy} onClick={submit}>
          {busy ? 'Sending…' : 'Suggest this store'}
        </button>
        <button type="button" style={btnGhost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
